import {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo
} from 'react';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';
import { getInitialMessage } from '../shared/utils/session-utils';
import { checkGenerateQuestionsNeeded } from '../shared/utils/suggestions';
import { announceAssistantReady } from '../platform/events/lifecycle';
import { logger } from '../shared/utils/logger';
// Avenue B: the messages store now lives in a Web Worker. This hook is a near
// drop-in for useIndexedDB('messages'); the callback-driven cursorAtomic ops are
// replaced by the declarative insertIfAbsent / clearMessages / cleanOrphans ops.
import { useMessageStore } from '../platform/persistence/messageStore';
import { conversationRecord, isNewSessionGeneration } from '../platform/session/session';
import { FLAGS } from '../platform/config/flags';
import { isCustomerTypeAllowed } from '../platform/config/surface';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
// Avenue A: identity-preserving merge so worker reads don't re-render the whole list.
import { reconcileMessages } from '../shared/utils/message-reconcile';
import { normalizeLegacyMessageType } from '../shared/message';

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @param { Object } session
 * @param { { getState: () => object } } [assistantStore] - the assistant store. When passed, this
 *   hook publishes its message-history + ISQ state into it directly (see the publish effects near
 *   the return), so the provider no longer bridges these values via effects. Optional so the hook
 *   stays usable without a store (e.g. unit tests that renderHook it in isolation). Named to avoid
 *   colliding with `store` below, which is the message-persistence store (useMessageStore).
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (
  withPersistence = true, session, assistantStore, submitGenerateQuestions = () => {},
) => {
  const {
    isConsumerApp,
    channel,
    customer,
    deliveryZip
  } = useContext(ExperienceContext);

  const isDynamicIsqMasterEnabled = useConfigService(FLAGS.dynamicIsqMasterEnabled);
  const isDynamicIsqDtFsEnabled = useConfigService(FLAGS.dynamicIsqDtEnabled);
  const isDynamicIsqMwFsEnabled = useConfigService(FLAGS.dynamicIsqMwEnabled);
  const isDynamicIsqAppFsEnabled = useConfigService(FLAGS.dynamicIsqAppEnabled);
  const isDynamicIsqB2BEnabled = useConfigService(FLAGS.dynamicIsqB2bEnabled);
  const isDynamicIsqValidCustomerType = isCustomerTypeAllowed(customer?.type, isDynamicIsqB2BEnabled);

  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const hasHistoryInitialized = useRef(false);
  const initializationLock = useRef(false);
  const prevCustomerType = useRef(customer?.type);
  const [messages, setMessages] = useState([getInitialMessage(session.sessionId)]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCallGeneratedQuestions, setCallToGenerateQuestions] = useState(false);

  // Scoped to this holder's session: a tab open across a session rotation must not render
  // a peer tab's conversation (see messageStore#useMessageStore).
  const store = useMessageStore(session.sessionId);

  const loadIDBMessages = () => {
    setIsLoading(true);
    return store.getAll()
      .then((savedMessages) => {
        // Coerce legacy persisted types (uppercase 'AGENT' → 'agent') on read, before
        // sort/reconcile, so rows written before the casing normalization still render.
        const normalized = savedMessages.map(normalizeLegacyMessageType);
        const sortedMessages = [...normalized].sort((first, second) => {
          if (first.isInitialMessage) return -1;
          if (second.isInitialMessage) return 1;
          return 0;
        });
        setMessages((prev) => reconcileMessages(prev, sortedMessages));
        setIsLoading(false);
        return normalized;
      });
  };

  const updateMessage = async (message, updates, reload = false) => {
    /* global structuredClone */
    const updatedMessage = { ...structuredClone(message), ...updates, updatedAt: Date.now() };

    await store.update(updatedMessage);
    if (reload) await loadIDBMessages();
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  const saveMessage = async (newMessage) => {
    // DEFERRED (WS-8.3, needs a compliance spec before building): in live-agent mode,
    // redact PII from messages before persisting them here. The text content-policy
    // (card/SSN) already lives in shared/utils/redaction.js, but agent-mode PII
    // redaction-on-persist is an unspecified feature: (1) what counts as PII (today
    // redaction covers only cards + SSN — agent mode may need emails/phone/address/
    // names); (2) redact only the persisted copy, or also the copy sent to the agent;
    // (3) UX — a redacted persisted message means the shopper sees XXXX in their own
    // history. Do NOT implement without deciding these. See redaction.js.
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      updatedAt: Date.now(),
    };
    if (!isStorageAvailable.current) {
      // Functional updater: reading `messages` from the closure here appended to
      // a stale array when several saves landed in one tick, dropping messages.
      setMessages((curr) => [...curr, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.sessionId) return Promise.reject(new Error('Session ID not available'));

    return store.add(updatedMessage)
      .then(loadIDBMessages)
      .then((savedMessages) => savedMessages.at(-1));
  };

  /**
   * Cross-tab safe variant of saveMessage for events that fan out to every
   * tab (e.g. inbound Sprinklr agent messages). Uses a single IndexedDB
   * `readwrite` transaction to check for an existing record with the same
   * `messageId` and add only if absent. IDB serializes overlapping
   * `readwrite` transactions across browsing contexts, so exactly one tab
   * persists the row.
   *
   * Resolves to the saved message when this call wrote it, or `null` when a
   * peer tab had already written the same `messageId`.
   */
  const saveMessageIfNew = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      updatedAt: Date.now(),
    };

    if (!isStorageAvailable.current) {
      if (!updatedMessage.messageId) {
        setMessages((curr) => [...curr, updatedMessage]);
        return updatedMessage;
      }
      let didAppend = false;
      setMessages((curr) => {
        if (curr.some((msg) => msg.messageId === updatedMessage.messageId)) return curr;
        didAppend = true;
        return [...curr, updatedMessage];
      });
      return didAppend ? updatedMessage : null;
    }
    if (!session.sessionId) throw new Error('Session ID not available');
    /*  Scenario: The incoming message has no messageId (e.g., system messages
    like LIVE_AGENT_CONNECTING_MESSAGE). There's nothing to deduplicate against,
    so it falls back to a plain iDBAdd — identical to saveMessage. Then reloads all messages from IDB
    into state and returns the last one. */
    if (!updatedMessage.messageId) {
      return store.add(updatedMessage)
        .then(loadIDBMessages)
        .then((all) => all.at(-1));
    }

    // Cross-tab dedup add: the cursor scan + conditional insert run atomically
    // inside the worker (see idb-core#insertIfAbsent), matching on `messageId`.
    const wasInserted = await store.insertIfAbsent(updatedMessage, 'messageId');
    await loadIDBMessages();
    if (wasInserted) broadCastMessageUpdate();
    return wasInserted ? updatedMessage : null;
  };

  // Delete every non-initial message. The cursor walk now runs in the worker.
  const clearMessageHistory = () => store
    .clearMessages({ keepInitial: true })
    .then(loadIDBMessages);

  const clearHistory = debounce(50, clearMessageHistory); // 50ms delay for consistency

  // Startup prune (other-session rows + duplicate initials; blank b2b suggested
  // questions on the kept initial). The per-row logic now runs in the worker
  // (idb-core#cleanOrphans), which also stamps updatedAt on the b2b-blanked row.
  const cleanIndexDb = () => store.cleanOrphans({
    sessionId: session.sessionId,
    customerType: customer?.type,
  });

  /**
   * Drop a conversation left over from a previous session generation, then re-watermark.
   *
   * The conversation domain PULLS this rather than being told: identity is written by the
   * Shell's mount effect, message history mounts later in a lazy chunk, and any transient
   * signal — an event, a threaded prop — is one a document can miss (closed early, chunk
   * failed) with no way to recover it, because the reset has already refreshed the timestamp
   * that made the session stale. The watermark is persisted, so a clear that was owed is still
   * owed on the next load. See platform/session#isNewSessionGeneration for why the sessionId
   * cannot answer this on its own.
   *
   * `keepInitial: false`, unlike the user's "Clear conversation": a new generation gets a
   * genuinely fresh opening message, stamped with the current session and re-offered suggested
   * questions. That is what the two reset reasons that DO change the sessionId already do via
   * cleanOrphans, so this keeps all three behaving alike.
   */
  const clearHistoryFromPreviousSession = async () => {
    const { lastSessionStart } = conversationRecord.get();
    const sessionStart = session.timestamp ?? null;

    if (isNewSessionGeneration(lastSessionStart, sessionStart)) {
      await store.clearMessages({ keepInitial: false });
    }
    if (lastSessionStart !== sessionStart) {
      conversationRecord.update({ lastSessionStart: sessionStart });
    }
  };

  const init = async () => {
    // Prevent multiple simultaneous initializations
    if (initializationLock.current) return;
    initializationLock.current = true;
    try {
      isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';
      // Before the prune and the initial-message check below, so a cleared history re-seeds
      // its opening message and re-runs the suggested-questions decision.
      await clearHistoryFromPreviousSession();
      await cleanIndexDb();

      let savedMessages = await store.getAll();
      const initialMessage = savedMessages.find((msg) => msg.isInitialMessage);

      if (!initialMessage) {
        await store.add({ ...getInitialMessage(session.sessionId), updatedAt: Date.now() });
      }
      // Retrieving get all messages again to ensure we have the initial message
      savedMessages = await store.getAll();
      const shouldCallGenerateQuestions = checkGenerateQuestionsNeeded({
        savedMessages,
        channel,
        isConsumerApp,
        isDynamicIsqMasterEnabled, // Dynamic ISQ Master Flag
        isDynamicIsqDtFsEnabled, // Desktop Dynamic ISQ Flag
        isDynamicIsqMwFsEnabled, // Mobile Web Dynamic ISQ Flag
        isDynamicIsqAppFsEnabled, // App Dynamic ISQ Flag
        isDynamicIsqValidCustomerType // Valid Customer Type Flag
      });
      if (shouldCallGenerateQuestions) {
        setCallToGenerateQuestions(true);

      }

      hasHistoryInitialized.current = true;
      // The single announcement to the host page's entry points, fired at the moment we can
      // actually take a question. Deliberately the ONLY one — see the loop warning on the
      // orchestrator's broadcast handler for why re-announcing off drawer traffic is unsafe.
      announceAssistantReady();

      await loadIDBMessages();
    } finally {
      initializationLock.current = false;
    }
  };

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = createBroadcastChannel(CHANNELS.newMessage);
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = () => {
            logger.log('Received message update broadcast, reloading messages from IndexedDB', messages);
            loadIDBMessages();
          };
        }
      })
      .catch(() => { });

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
        maBroadcastChannel.current = null;
      }
      // Reset initialization lock on unmount
      initializationLock.current = false;
    });
  }, []);

  // Asking for suggested questions is message history's decision; ISSUING the query — and
  // deciding WHEN it may be issued — is the query layer's (Seq F.d/G.1). The trigger arrives
  // as a capability from the composition root, so this core hook neither imports the query
  // layer nor calls `useStoreId()`. Dropping that call is what takes Apollo off the
  // warm-while-closed path: useStoreId → useStore → useMembershipInformation → data-sources.
  // The localization wait it used to gate on now lives in the block, which queues until the
  // shopper's store is known.
  useEffect(() => {
    if (isCallGeneratedQuestions) {
      submitGenerateQuestions({ isConsumerApp, channel, deliveryZip });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallGeneratedQuestions]);

  /**
   * Patch the opening message with the generated suggested questions.
   *
   * The query layer calls this when its `generateQuestions` operation resolves — it used to be
   * an effect keyed on that operation's response, which is why this hook had to own the
   * operation. Fetching and committing are different jobs; only the commit is message
   * history's, and only it needs the persistence store.
   *
   * @param {object} response  raw `generateQuestions` data.
   */
  const commitSuggestedQuestions = async (response) => {
    const dbMessages = await store.getAll();
    const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);
    if (!initialMessage) return;

    initialMessage.content.relatedQuestions = {
      suggestedQuestions: response?.generateQuestions?.suggestedQuestions || [],
      followUpQuestions: [],
      clarifyingQuestions: []
    };
    initialMessage.updatedAt = Date.now();

    await store.update(initialMessage)
      .then(loadIDBMessages)
      .finally(() => { broadCastMessageUpdate(); });
    setCallToGenerateQuestions(false);
  };

  useEffect(() => {
    const prev = prevCustomerType.current;
    prevCustomerType.current = customer?.type;

    if (prev !== 'b2b' && customer?.type === 'b2b') {
      if (isStorageAvailable.current) {
        store.getAll().then((dbMessages) => {
          const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);
          if (!initialMessage) return;
          if (initialMessage?.content?.relatedQuestions?.suggestedQuestions?.length) {
            initialMessage.content.relatedQuestions.suggestedQuestions = [];
            initialMessage.updatedAt = Date.now();
            store.update(initialMessage).then(loadIDBMessages).then(broadCastMessageUpdate);
          }
        });
      } else {
        setMessages((current) => current.map((msg) => (
          // eslint-disable-next-line max-len
          msg.isInitialMessage ? { ...msg, content: { ...msg.content, relatedQuestions: { suggestedQuestions: [], followUpQuestions: [], clarifyingQuestions: [] } } } : msg
        )));
      }
    }
  }, [customer?.type]);

  const { lastBotMessage, lastUserMessage } = useMemo(() => ({
    lastBotMessage: messages.findLast(({ type }) => type === 'bot'),
    lastUserMessage: messages.findLast(({ type }) => type === 'user'),
  }), [messages]);

  // Publish message-history + ISQ state into the assistant store at its source
  // (WS-10.1). These originate in this hook, so it writes them into the store
  // directly (like QueryLayerBlock/useFileUpload publish their own state) — retiring
  // the six provider effect-bridges that used to mirror this hook's return values.
  // The hook still returns the same shape for the provider's own callbacks
  // (onSurveyReceived, onImageReceived, askUserQuestion ctx). Guarded on
  // `assistantStore` so an isolated renderHook (no store) is a no-op.
  // `hasHistoryInitialized` is a ref, so we key on its render-snapshot value —
  // exactly what the provider effect received before.
  const historyInitialized = hasHistoryInitialized.current;
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setMessages(messages);
  }, [messages, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setLastBotMessage(lastBotMessage);
  }, [lastBotMessage, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setLastUserMessage(lastUserMessage);
  }, [lastUserMessage, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setHistoryLoading(isLoading);
  }, [isLoading, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setWereQuestionsGenerated(historyInitialized);
  }, [historyInitialized, assistantStore]);

  return {
    messages,
    lastBotMessage,
    lastUserMessage,
    saveMessage,
    saveMessageIfNew,
    historyLoading: isLoading,
    hasHistoryInitialized: hasHistoryInitialized.current,
    clearHistory,
    updateMessage,
    broadCastMessageUpdate,
    // Preserve the hook's public shape for consumers; now worker-backed.
    iDBGetAll_messages: store.getAll,
    iDBUpdate_message: store.update,
    loadIDBMessages,
    // Driven by the query-layer block when its generateQuestions operation resolves.
    commitSuggestedQuestions,
  };
};
